// Admin Audit store — signal-based, follows departments.ts pattern.
// GET /api/admin/audit?limit&cursor&entityType&action&userId
//
// Paging is KEYSET (cursor) rather than offset: the backend returns an opaque
// `next_cursor` that pins the (created_at, id) boundary of the last row, so a
// row inserted between page fetches can never duplicate or skip across pages.
// We keep a cursor stack so the UI can still page backwards: index i holds the
// cursor that produced page i (page 0 = null), and `next_cursor` is pushed when
// the user advances.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../lib/api";

export interface AuditEntry {
  id: number;
  user: { id: number; name: string; email: string } | null;
  entity_type: string | null;
  entity_id: number | null;
  action: string | null;
  changes: unknown;
  created_at: string;
}

export interface AuditFilters {
  entityType?: string;
  action?: string;
  userId?: number;
  limit: number;
}

interface AuditResponse {
  items: AuditEntry[];
  total: number;
  next_cursor: string | null;
}

interface State {
  loading: boolean;
  items: AuditEntry[];
  total: number;
  error: string | null;
  filters: AuditFilters;
  /** Cursor that produced the page BEFORE the current one, for each visited
   *  page. cursorStack[pageIndex] is the cursor used to fetch the current page
   *  (null for page 0). */
  cursorStack: (string | null)[];
  pageIndex: number;
  nextCursor: string | null;
}

const defaultFilters: AuditFilters = { limit: 50 };
const initial: State = {
  loading: true,
  items: [],
  total: 0,
  error: null,
  filters: defaultFilters,
  cursorStack: [null],
  pageIndex: 0,
  nextCursor: null,
};
const adminAuditSignal: Signal<State> = signal(initial);
let inFlight: Promise<void> | null = null;

interface LoadOpts {
  filters: AuditFilters;
  cursor: string | null;
  cursorStack: (string | null)[];
  pageIndex: number;
}

async function load(opts: LoadOpts): Promise<void> {
  adminAuditSignal.value = { ...adminAuditSignal.value, loading: true };
  const { filters, cursor, cursorStack, pageIndex } = opts;
  const params = new URLSearchParams();
  params.set("limit", String(filters.limit));
  if (cursor) params.set("cursor", cursor);
  if (filters.entityType) params.set("entityType", filters.entityType);
  if (filters.action) params.set("action", filters.action);
  if (filters.userId != null) params.set("userId", String(filters.userId));

  try {
    const data = await api<AuditResponse>(
      `/api/admin/audit?${params.toString()}`
    );
    adminAuditSignal.value = {
      loading: false,
      items: data.items,
      total: data.total,
      error: null,
      filters,
      cursorStack,
      pageIndex,
      nextCursor: data.next_cursor,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    adminAuditSignal.value = {
      ...adminAuditSignal.value,
      loading: false,
      error: msg,
    };
  } finally {
    inFlight = null;
  }
}

export function useAdminAudit(): State {
  useEffect(() => {
    if (!inFlight && adminAuditSignal.value.loading) {
      inFlight = load({
        filters: adminAuditSignal.value.filters,
        cursor: null,
        cursorStack: [null],
        pageIndex: 0,
      });
    }
  }, []);
  return adminAuditSignal.value;
}

/** Apply a filter change. Always resets paging back to the first page. */
export function setAuditFilters(partial: Partial<AuditFilters>): void {
  const filters = { ...adminAuditSignal.value.filters, ...partial };
  inFlight = load({ filters, cursor: null, cursorStack: [null], pageIndex: 0 });
}

/** Advance to the next page using the server-provided next_cursor. */
export function nextAuditPage(): void {
  const s = adminAuditSignal.value;
  if (!s.nextCursor) return;
  inFlight = load({
    filters: s.filters,
    cursor: s.nextCursor,
    cursorStack: [...s.cursorStack, s.nextCursor],
    pageIndex: s.pageIndex + 1,
  });
}

/** Go back one page using the cursor stack. */
export function prevAuditPage(): void {
  const s = adminAuditSignal.value;
  if (s.pageIndex === 0) return;
  const cursorStack = s.cursorStack.slice(0, -1);
  const pageIndex = s.pageIndex - 1;
  inFlight = load({
    filters: s.filters,
    cursor: cursorStack[pageIndex] ?? null,
    cursorStack,
    pageIndex,
  });
}
